<script setup>
import { ref, computed } from 'vue';
import {
  calculateVoltage,
  calculateAmperage,
  calculateTravelSpeed,
  calculateHeatInput,
} from './lib/logic.js';

// State
const solveFor = ref('');
const format = ref('metric');
const voltage = ref('');
const amperage = ref('');
const travelSpeed = ref('');
const heatInput = ref('');
const hasCalculated = ref(false);

// Solve options
const solveOptions = [
  { value: 'voltage', label: 'Voltage' },
  { value: 'amperage', label: 'Amperage' },
  { value: 'travel-speed', label: 'Travel Speed' },
  { value: 'heat-input', label: 'Heat Input' },
];

// Computed values
const formatUnit = computed(() => (format.value === 'metric' ? 'mm' : 'in'));

const formulaDisplay = computed(() => {
  switch (solveFor.value) {
    case 'voltage':
      return 'V = (H × 1000 × S) / (60 × A)';
    case 'amperage':
      return 'A = (H × 1000 × S) / (60 × V)';
    case 'travel-speed':
      return 'S = (60 × V × A) / (1000 × H)';
    case 'heat-input':
      return 'H = (60 × V × A) / (1000 × S)';
    default:
      return '';
  }
});

const errorMessage = ref('');

// Event handlers
function handleSolveForChange() {
  hasCalculated.value = false;
  errorMessage.value = '';
}

function calculate() {
  const v = voltage.value === '' ? null : Number(voltage.value);
  const a = amperage.value === '' ? null : Number(amperage.value);
  const s = travelSpeed.value === '' ? null : Number(travelSpeed.value);
  const h = heatInput.value === '' ? null : Number(heatInput.value);

  let result = null;

  switch (solveFor.value) {
    case 'voltage':
      if (h === null || s === null || a === null) {
        errorMessage.value = 'Please fill out all fields';
        return;
      }
      result = calculateVoltage(h, s, a);
      if (result !== null) {
        voltage.value = result.toString();
      }
      break;

    case 'amperage':
      if (h === null || s === null || v === null) {
        errorMessage.value = 'Please fill out all fields';
        return;
      }
      result = calculateAmperage(h, s, v);
      if (result !== null) {
        amperage.value = result.toString();
      }
      break;

    case 'travel-speed':
      if (v === null || a === null || h === null) {
        errorMessage.value = 'Please fill out all fields';
        return;
      }
      result = calculateTravelSpeed(v, a, h);
      if (result !== null) {
        travelSpeed.value = result.toString();
      }
      break;

    case 'heat-input':
      if (v === null || a === null || s === null) {
        errorMessage.value = 'Please fill out all fields';
        return;
      }
      result = calculateHeatInput(v, a, s);
      if (result !== null) {
        heatInput.value = result.toString();
      }
      break;

    default:
      errorMessage.value = 'Please select what to solve for';
      return;
  }

  if (result === null) {
    errorMessage.value = 'Invalid calculation (division by zero or invalid values)';
    return;
  }

  errorMessage.value = '';
  hasCalculated.value = true;
}

function isFieldDisabled(field) {
  return solveFor.value === field;
}

function isFieldBold(field) {
  return hasCalculated.value && solveFor.value === field;
}
</script>

<template>
  <div class="hb-container" style="max-width: 340px; margin: 50px auto">
    <h2 class="hb-page-title">Heat Input Calculator</h2>

    <ul class="weld-calculator">
      <li class="weld-calculator-twocol">
        <label for="solve-for">Solve For:</label>
        <select id="solve-for" v-model="solveFor" class="hb-select" @change="handleSolveForChange">
          <option value="">--Select--</option>
          <option v-for="option in solveOptions" :key="option.value" :value="option.value">
            {{ option.label }}
          </option>
        </select>
      </li>

      <li v-if="solveFor" class="weld-calculator-twocol">
        <div>Format:</div>
        <div>
          <input id="sae" v-model="format" type="radio" value="sae" name="format" />
          <label for="sae">SAE</label>
          <input id="metric" v-model="format" type="radio" value="metric" name="format" />
          <label for="metric">Metric</label>
        </div>
      </li>

      <li v-if="solveFor" class="weld-calculator-twocol">
        <label for="voltage">Voltage:</label>
        <input
          id="voltage"
          v-model="voltage"
          type="number"
          class="hb-input"
          :disabled="isFieldDisabled('voltage')"
          :class="{ 'disabled-field': isFieldDisabled('voltage') }"
        />
      </li>

      <li v-if="solveFor" class="weld-calculator-twocol">
        <label for="amperage">Amperage:</label>
        <input
          id="amperage"
          v-model="amperage"
          type="number"
          class="hb-input"
          :disabled="isFieldDisabled('amperage')"
          :class="{ 'disabled-field': isFieldDisabled('amperage') }"
        />
      </li>

      <li v-if="solveFor" class="weld-calculator-twocol">
        <label for="travel-speed">
          Travel Speed:<br />
          ({{ formatUnit }}/min)
        </label>
        <input
          id="travel-speed"
          v-model="travelSpeed"
          type="number"
          class="hb-input"
          :disabled="isFieldDisabled('travel-speed')"
          :class="{ 'disabled-field': isFieldDisabled('travel-speed') }"
        />
      </li>

      <li v-if="solveFor" class="weld-calculator-twocol">
        <label for="heat-input">
          Heat Input:<br />
          (kj/{{ formatUnit }})
        </label>
        <input
          id="heat-input"
          v-model="heatInput"
          type="number"
          class="hb-input"
          :disabled="isFieldDisabled('heat-input')"
          :class="{ 'disabled-field': isFieldDisabled('heat-input') }"
        />
      </li>

      <li v-if="solveFor">
        <button type="button" class="hb-button" @click="calculate">Calculate</button>
        <div v-if="errorMessage" class="error-message">{{ errorMessage }}</div>

        <div v-if="hasCalculated" class="results-output">
          <div v-if="formulaDisplay" class="formula-display">{{ formulaDisplay }}</div>

          <div :class="{ 'result-bold': isFieldBold('voltage') }">
            <span class="result-value">{{ voltage || '0' }}</span>
            <span class="result-label formula-v">V = Volts</span>
          </div>

          <div :class="{ 'result-bold': isFieldBold('amperage') }">
            <span class="result-value">{{ amperage || '0' }}</span>
            <span class="result-label formula-a">A = Amperes</span>
          </div>

          <div :class="{ 'result-bold': isFieldBold('travel-speed') }">
            <span class="result-value">{{ travelSpeed || '0' }}</span>
            <span class="result-label formula-s">S = Travel Speed ({{ formatUnit }}/min)</span>
          </div>

          <div :class="{ 'result-bold': isFieldBold('heat-input') }">
            <span class="result-value">{{ heatInput || '0' }}</span>
            <span class="result-label formula-h">H = Heat Input (kj/{{ formatUnit }})</span>
          </div>
        </div>
      </li>
    </ul>
  </div>
</template>

<style scoped>
.weld-calculator {
  font-size: 18px;
  list-style-type: none;
  margin: 20px auto;
  padding: 0;
}

.weld-calculator > li {
  margin-top: 20px;
}

.weld-calculator-twocol {
  display: flex;
  gap: 12px;
  align-items: center;
}

.weld-calculator-twocol > label,
.weld-calculator-twocol > div:first-child {
  width: 45%;
  min-width: 45%;
}

.weld-calculator-twocol > select,
.weld-calculator-twocol > input,
.weld-calculator-twocol > div:last-child {
  width: 55%;
  flex: 1;
}

.weld-calculator-twocol input[type='radio'] {
  height: 18px;
  width: 18px;
  vertical-align: text-top;
}

.weld-calculator-twocol input[type='radio']:first-child + label {
  margin-right: 10px;
}

.hb-select,
.hb-input {
  font-size: 18px;
}

.disabled-field {
  background-color: #fff;
  border: 1px solid #fff;
  cursor: not-allowed;
}

.error-message {
  background-color: #ffc7c7;
  border: 1px solid #c3a2a2;
  margin-top: 20px;
  padding: 10px;
  text-align: center;
}

.results-output {
  border: 1px solid #ddd;
  padding: 15px;
  margin-top: 30px;
}

.results-output > div {
  margin-top: 10px;
}

.results-output > div:first-child {
  margin-top: 0;
}

.formula-display {
  font-weight: bold;
  margin-bottom: 10px;
}

.result-bold {
  font-weight: bold;
}

.result-value {
  display: inline-block;
  min-width: 60px;
}

.result-label {
  margin-left: 8px;
}

.formula-v {
  color: red;
}
.formula-a {
  color: green;
}
.formula-s {
  color: blue;
}
.formula-h {
  color: orange;
}
</style>
